import { React, useContext, useMemo, useState } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Alerta, PopupDialog } from '../components/alerta';
import PageHeader from '../components/pageHeader';
import styles from '../pages/css/menu.module.css';
import DadosUsuario from '../utils/context';
import { fetchFlask } from '../utils/fetchFlask';
import { getSectionByKey, getSectionPath, getVisibleItems, getVisibleSections, hasMenuAccess } from '../utils/menuSections';

const menuCardSx = {
    border: '1px solid var(--app-line)',
    borderRadius: '20px',
    background: 'var(--app-surface)',
    boxShadow: 'var(--app-shadow)',
    backdropFilter: 'blur(22px)',
    color: 'var(--app-text)',
    transition: 'transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
        borderColor: 'rgba(255, 163, 0, 0.55)',
        background: 'var(--app-surface-strong)',
        transform: 'translateY(-2px)'
    }
};

const Menu = () => {
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [textoDialog, setTextoDialog] = useState({});
    const [openAlerta, setOpenAlerta] = useState(false);

    const navigate = useNavigate();
    const { section } = useParams();
    const { autorizacoes = [] } = useContext(DadosUsuario);

    const visibleSections = useMemo(() => getVisibleSections(autorizacoes), [autorizacoes]);
    const selectedSection = section ? getSectionByKey(section) : null;
    const canAccessSelected = selectedSection && hasMenuAccess(selectedSection, autorizacoes);
    const visibleSelectedItems = useMemo(() =>
        selectedSection ? getVisibleItems(selectedSection.items, autorizacoes) : []
    , [selectedSection, autorizacoes]);
    const accessibleLinkRoutes = useMemo(() =>
        visibleSections.flatMap((sec) => getVisibleItems(sec.items, autorizacoes)
            .filter((item) => item.type === 'link')
            .map((item) => item.page))
    , [visibleSections, autorizacoes]);
    const selectedSectionSingleItem = visibleSelectedItems.length === 1 ? visibleSelectedItems[0] : null;

    function handleButtonClick (e, titulo, texto, funcao) {
        e.preventDefault();
        setOpenDialog(true);
        setTextoDialog({ titulo, texto, funcao });
    }

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    async function submitFaturasNeoenergia () {
        const response = await fetchFlask('lerFaturasNeoenergia');
        handleAlerta(response);
    }

    async function submitCSVPagar () {
        const response = await fetchFlask('gerarContasPagar');
        handleAlerta(response);
    }

    const getActionHandler = (item) => {
        if (item.action === 'lerFaturasNeoenergia') {
            return (e) => handleButtonClick(
                e,
                'Leitura de Faturas',
                'Tem certeza que deseja realizar a leitura de faturas?',
                submitFaturasNeoenergia
            );
        }

        if (item.action === 'gerarContasPagar') {
            return (e) => handleButtonClick(
                e,
                'CSV Pagar',
                'Tem certeza que deseja gerar o CSV de pagar e enviar por e-mail?',
                submitCSVPagar
            );
        }

        return null;
    };

    const getSectionTarget = (sec) => {
        const visibleItems = getVisibleItems(sec.items, autorizacoes);

        if (visibleItems.length === 1 && visibleItems[0].type === 'link') {
            return `/${visibleItems[0].page}`;
        }

        return getSectionPath(sec);
    };

    const getItemDescription = (item) => {
        if (item.type === 'link') {
            return 'Abrir página';
        }

        return 'Executar ação';
    };

    const renderSection = (sec) => {
        const Icon = sec.icon;
        return (
            <Box className={styles.column} key={sec.key} sx={{ width: '100%' }}>
                <PageHeader icon={Icon}>{sec.title}</PageHeader>
                <Box display='grid' gridTemplateColumns='repeat(auto-fit, minmax(230px, 1fr))' gap={2}>
                    {sec.items.map((item) => {
                        if (!hasMenuAccess(item, autorizacoes)) {
                            return null;
                        }

                        const itemKey = item.type === 'link' ? `${sec.key}-${item.page}` : `${sec.key}-${item.action}`;
                        const handleClick = item.type === 'link'
                            ? () => navigate(`/${item.page}`)
                            : getActionHandler(item);
                        const ItemIcon = item.icon || Icon;

                        return (
                            <Card key={itemKey} sx={menuCardSx}>
                                <CardActionArea onClick={handleClick} sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
                                            <Stack direction='row' spacing={1} alignItems='center'>
                                                <ItemIcon color='primary' />
                                                <Typography variant='h6' textAlign='left'>{item.text}</Typography>
                                            </Stack>
                                            <ChevronRightRoundedIcon color='action' />
                                        </Stack>
                                        <Typography variant='body2' color='text.secondary' textAlign='left' sx={{ mt: 1 }}>
                                            {getItemDescription(item)}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderHome = () => (
        <>
            <PageHeader>SETTA CORPORATIVO</PageHeader>
            <Box display='grid' gridTemplateColumns='repeat(auto-fit, minmax(230px, 1fr))' gap={2}>
                {visibleSections.map((sec) => {
                    const Icon = sec.icon;
                    return (
                        <Card key={sec.key} sx={menuCardSx}>
                            <CardActionArea onClick={() => navigate(getSectionTarget(sec))} sx={{ height: '100%' }}>
                                <CardContent>
                                    <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
                                        <Stack direction='row' spacing={1} alignItems='center' mb={1}>
                                            <Icon color='primary' />
                                            <Typography variant='h6'>{sec.title}</Typography>
                                        </Stack>
                                        <ChevronRightRoundedIcon color='action' />
                                    </Stack>
                                    <Typography variant='body2' color='text.secondary'>
                                        {sec.description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>
        </>
    );

    if (!section && accessibleLinkRoutes.length === 1) {
        return <Navigate to={`/${accessibleLinkRoutes[0]}`} replace />;
    }

    if (section && canAccessSelected && selectedSection.landing) {
        return <Navigate to={getSectionPath(selectedSection)} replace />;
    }

    if (section && canAccessSelected && selectedSectionSingleItem?.type === 'link') {
        return <Navigate to={`/${selectedSectionSingleItem.page}`} replace />;
    }

    return (
        <Box className={styles.row}>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PopupDialog
                titulo={textoDialog.titulo}
                texto={textoDialog.texto}
                open={openDialog}
                handleClose={() => setOpenDialog(false)}
                funcao={textoDialog.funcao}
            />

            <Box className={styles.column} sx={{ flex: 1 }}>
                {!section && renderHome()}
                {section && canAccessSelected && renderSection(selectedSection)}
                {section && !canAccessSelected && (
                    <Box>
                        <Typography variant='h5'>Area nao encontrada</Typography>
                        <Typography color='text.secondary' sx={{ mt: 1 }}>Escolha uma area valida no menu lateral.</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Menu;
